<script setup lang="ts">
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';

import type { AccommodationType } from '@libs/trip-planner-interfaces';

import { useActivityDetailsTicketsStore } from '../../stores/activityDetailsTicketsStore';
import { ActivityDetailsTemplateId } from '../../models/activityDetailsTemplateId';

import Ticket from './Ticket.vue';

interface ActivityDetailsTicketsProps {
  productCode: string;
  activityType: AccommodationType;
  isAlternative: boolean;
}
const props = defineProps<ActivityDetailsTicketsProps>();

const activityDetailsTicketsStore = useActivityDetailsTicketsStore();
const recipeStore = useRecipeStore();
</script>

<template>
  <div class="activity-details-ticket">
    <span class="section-title">{{ $t('planner.select_ticket') }}</span>
    <span class="section-subtitle">
      {{ $t('planner.days_during_my_stay') }}
    </span>

    <div class="activities-dates-wrapper">
      <button
        v-for="(date, index) in activityDetailsTicketsStore.bookableDays"
        :key="index"
        class="activity-date"
        :class="{
          selected: activityDetailsTicketsStore.isSelectedDate(date),
          disabled:
            !activityDetailsTicketsStore.thereIsTicketAvailability(index),
        }"
        @click="activityDetailsTicketsStore.selectDate(date)"
      >
        {{ $t('planner.days') }} {{ index + 1 }}
      </button>
    </div>
    <div
      v-if="activityDetailsTicketsStore.selectedDateOffset !== undefined"
      :id="ActivityDetailsTemplateId.TicketRates"
    >
      <Ticket
        v-for="(
          ticketRate, index
        ) in activityDetailsTicketsStore.ticketRatesInSelectedDate"
        :key="
          ticketRate.rateId + activityDetailsTicketsStore.selectedDateOffset
        "
        :ticket-rate="ticketRate"
        :product-code="props.productCode"
        :date-offset="activityDetailsTicketsStore.selectedDateOffset"
        :initial-rate-tickets="
          recipeStore.getSelectedTicketsForRate(
            ticketRate.rateId,
            activityDetailsTicketsStore.selectedDateOffset,
            props.isAlternative
          )
        "
        :activity-type="props.activityType"
        @update-selected-status="
          activityDetailsTicketsStore.updateTicketRateValidityStatus(
            $event,
            index
          )
        "
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.activity-details-ticket {
  padding-bottom: 0;
  @include for-less-or-equal-than-599-px {
    padding: 0 var(--spacing-16) var(--spacing-24) var(--spacing-16);
  }

  .section-title {
    border-top: 1px solid var(--color-neutral-30);
    margin-bottom: var(--spacing-8);
    padding-top: var(--spacing-32);
    display: block;
    @include font-heading-5;
    @include for-less-or-equal-than-599-px {
      padding-top: var(--spacing-16);
    }
  }

  .section-subtitle {
    margin-bottom: var(--spacing-24);
    display: block;
    @include font-label-l-light;
  }

  .activities-dates-wrapper {
    overflow: auto;
    overflow: overlay;
    display: flex;
    margin-bottom: var(--spacing-32);

    .activity-date {
      border-radius: var(--border-radius-12);
      padding: var(--spacing-24) 0;
      margin-right: var(--spacing-12);
      height: 100%;
      width: 150px;
      background-color: var(--color-white);
      border: 1px solid var(--color-neutral-40);
      box-sizing: border-box;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all var(--transition-short);
      white-space: nowrap;
      @include font-label-xl-bold;

      &.selected {
        background-color: var(--color-neutral-70);
        color: var(--color-white);
      }

      &.disabled {
        background-color: var(--color-neutral-30);
        color: var(--color-neutral-70);
        pointer-events: none;
        opacity: 0.26;
      }
      &:not(.selected) {
        @include on-hover {
          background-color: var(--color-neutral-30);
        }
      }
    }
  }

  #ticket-rates {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-24);
  }
}
</style>
